import { Checkbox } from '@/components/ui/checkbox'
import { FormField } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import Select from 'app/frontend/base/components/Select'
import {
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FieldWrapper from './FieldWrapper'
import { FieldProps } from './types'
import { TFunction } from 'i18next'

export const transformOptions = (
  t: TFunction,
  name: string,
  value?: string[],
) => {
  return (value || []).map((option) => ({
    label: t(`settings.${name}.options.${option}`),
    value: option,
  }))
}

const SettingField = ({ layout = 'inline', name }: FieldProps<string>) => {
  const { t } = useTranslation()
  const { control, watch, setValue } = useFormContext()
  const setting: wowaudit.Setting<unknown> = watch(`settings.${name}`)

  const fieldElement = (
    field: ControllerRenderProps<FieldValues, `settings.${string}.value`>,
  ) => {
    if (setting.type === 'boolean') {
      return <Checkbox checked={field.value} onCheckedChange={field.onChange} />
    }

    if (setting.multiple) {
      const options = transformOptions(t, name, setting.options || [])
      return (
        <MultiSelect<string>
          options={options}
          value={setting.value as string[]}
          onChange={(selected) =>
            setValue(`settings.${name}.value`, selected, { shouldDirty: true })
          }
        />
      )
    }

    if (setting.options) {
      return (
        <Select
          value={setting.value as string}
          options={transformOptions(t, name, setting.options || [])}
          setValue={(value) =>
            setValue(`settings.${name}.value`, value, { shouldDirty: true })
          }
        />
      )
    }

    return (
      <Input
        type={setting.type}
        {...field}
        className={layout === 'box' ? `absolute right-3 top-1.5 w-24 z-20` : ''}
        value={(setting.value as string) ?? ''}
        onChange={(event) =>
          setValue(`settings.${name}.value`, event.target.value ?? '', {
            shouldDirty: true,
          })
        }
      />
    )
  }

  return (
    <div>
      <FormField
        control={control}
        name={`settings.${name}.value`}
        render={({ field }) => {
          return (
            <FieldWrapper
              layout={layout}
              label={t(`settings.${name}.label`)}
              description={t(`settings.${name}.description`)}
            >
              {fieldElement(field)}
            </FieldWrapper>
          )
        }}
      />
    </div>
  )
}

export default SettingField
